import type { AdminV2ModelRuntimePackage } from '@/admin-v2/models/model-types';
import {
  FREE_IMAGE_INPUT_MAX_BYTES,
  IMAGE_INPUT_MIN_HEIGHT,
  IMAGE_INPUT_MIN_WIDTH,
  PAID_IMAGE_INPUT_MAX_BYTES,
} from '@/ai/image/upload-limits';
import { workspaceAccessConfig } from '@/config/workspace-access';

type AdminV2ModelImageInputConstraintShape = NonNullable<
  AdminV2ModelRuntimePackage['inputLimits']['imageConstraints']
>;

export type AdminV2ModelImageInputConstraints =
  | AdminV2ModelImageInputConstraintShape
  | undefined;

export type AdminV2EffectiveImageInputConstraints = {
  minWidth: number;
  minHeight: number;
  freeMaxBytes: number;
  paidMaxBytes: number;
  allowedMimeTypes?: string[];
  maxWidth?: number;
  maxHeight?: number;
  maxMegapixels?: number;
  maxTotalBytes?: number;
  colorModes?: string[];
  allowedAspectRatios?: string[];
};

function minPositiveOrPlatform(
  platformLimit: number,
  modelLimit: number | undefined
) {
  return Math.min(platformLimit, modelLimit ?? platformLimit);
}

function normalizeAllowedList(values: string[] | undefined) {
  const normalized = Array.from(
    new Set(
      (values ?? [])
        .map((value) => value.trim().toLowerCase())
        .filter((value) => value.length > 0)
    )
  );
  return normalized.length > 0 ? normalized : undefined;
}

function mergeAllowedLists(
  left: string[] | undefined,
  right: string[] | undefined
) {
  const normalizedLeft = normalizeAllowedList(left);
  const normalizedRight = normalizeAllowedList(right);
  if (!normalizedLeft) return normalizedRight;
  if (!normalizedRight) return normalizedLeft;
  const rightSet = new Set(normalizedRight);
  const merged = normalizedLeft.filter((value) => rightSet.has(value));
  return merged.length > 0 ? merged : [];
}

export function resolveAdminV2EffectiveImageInputConstraints(
  modelConstraints?: AdminV2ModelImageInputConstraints
): AdminV2EffectiveImageInputConstraints {
  const modelMaxBytes = modelConstraints?.maxBytes;
  const allowedMimeTypes = normalizeAllowedList(
    modelConstraints?.allowedMimeTypes
  );
  const colorModes = normalizeAllowedList(modelConstraints?.colorModes);
  const allowedAspectRatios = normalizeAllowedList(
    modelConstraints?.allowedAspectRatios
  );

  return {
    minWidth: Math.max(
      IMAGE_INPUT_MIN_WIDTH,
      modelConstraints?.minWidth ?? IMAGE_INPUT_MIN_WIDTH
    ),
    minHeight: Math.max(
      IMAGE_INPUT_MIN_HEIGHT,
      modelConstraints?.minHeight ?? IMAGE_INPUT_MIN_HEIGHT
    ),
    freeMaxBytes: minPositiveOrPlatform(
      workspaceAccessConfig.paidEntitlementsEnabled
        ? FREE_IMAGE_INPUT_MAX_BYTES
        : PAID_IMAGE_INPUT_MAX_BYTES,
      modelMaxBytes
    ),
    paidMaxBytes: minPositiveOrPlatform(
      PAID_IMAGE_INPUT_MAX_BYTES,
      modelMaxBytes
    ),
    ...(allowedMimeTypes ? { allowedMimeTypes } : {}),
    ...(modelConstraints?.maxWidth
      ? { maxWidth: modelConstraints.maxWidth }
      : {}),
    ...(modelConstraints?.maxHeight
      ? { maxHeight: modelConstraints.maxHeight }
      : {}),
    ...(modelConstraints?.maxMegapixels
      ? { maxMegapixels: modelConstraints.maxMegapixels }
      : {}),
    ...(modelConstraints?.maxTotalBytes
      ? { maxTotalBytes: modelConstraints.maxTotalBytes }
      : {}),
    ...(colorModes ? { colorModes } : {}),
    ...(allowedAspectRatios ? { allowedAspectRatios } : {}),
  };
}

export function mergeAdminV2EffectiveImageInputConstraints(
  constraints: AdminV2ModelImageInputConstraints[]
): AdminV2EffectiveImageInputConstraints {
  return constraints
    .map(resolveAdminV2EffectiveImageInputConstraints)
    .reduce<AdminV2EffectiveImageInputConstraints>(
      (merged, current) => ({
        minWidth: Math.max(merged.minWidth, current.minWidth),
        minHeight: Math.max(merged.minHeight, current.minHeight),
        freeMaxBytes: Math.min(merged.freeMaxBytes, current.freeMaxBytes),
        paidMaxBytes: Math.min(merged.paidMaxBytes, current.paidMaxBytes),
        allowedMimeTypes: mergeAllowedLists(
          merged.allowedMimeTypes,
          current.allowedMimeTypes
        ),
        ...(merged.maxWidth || current.maxWidth
          ? {
              maxWidth: Math.min(
                merged.maxWidth ?? Number.POSITIVE_INFINITY,
                current.maxWidth ?? Number.POSITIVE_INFINITY
              ),
            }
          : {}),
        ...(merged.maxHeight || current.maxHeight
          ? {
              maxHeight: Math.min(
                merged.maxHeight ?? Number.POSITIVE_INFINITY,
                current.maxHeight ?? Number.POSITIVE_INFINITY
              ),
            }
          : {}),
        ...(merged.maxMegapixels || current.maxMegapixels
          ? {
              maxMegapixels: Math.min(
                merged.maxMegapixels ?? Number.POSITIVE_INFINITY,
                current.maxMegapixels ?? Number.POSITIVE_INFINITY
              ),
            }
          : {}),
        ...(merged.maxTotalBytes || current.maxTotalBytes
          ? {
              maxTotalBytes: Math.min(
                merged.maxTotalBytes ?? Number.POSITIVE_INFINITY,
                current.maxTotalBytes ?? Number.POSITIVE_INFINITY
              ),
            }
          : {}),
        colorModes: mergeAllowedLists(merged.colorModes, current.colorModes),
        allowedAspectRatios: mergeAllowedLists(
          merged.allowedAspectRatios,
          current.allowedAspectRatios
        ),
      }),
      resolveAdminV2EffectiveImageInputConstraints()
    );
}
